import { getExtension, getExtensionValue } from '@medplum/core';
import type {
  Extension,
  Patient,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import { EXTENSIONS } from './constants';
import { isQuestionnaireItemEnabled } from './questionnaireEnableWhen';
import {
  buildFhirTranslationExtension,
  QUESTIONNAIRE_ITEM_PATIENT_TEXT_URL,
  resolveLocalizedString,
  type ExtensionWithValueStringTranslations,
  type PrimitiveTranslationElement,
  type QuestionnaireItemAnswerOptionWithTranslations,
  type QuestionnaireItemWithTranslations,
} from './translation';

/**
 * Shared onboarding-questions Questionnaire for every practice (how they heard about us / why they
 * joined). Historical path segment — do not rename without migrating existing QuestionnaireResponses.
 */
export const ONBOARDING_QUESTIONS_QUESTIONNAIRE_URL = 'https://liza.health/fhir/questionnaire/patient-join-attribution';

/** Bumped when patient-facing item text extensions were added alongside clinical `item.text`. */
export const ONBOARDING_QUESTIONS_QUESTIONNAIRE_VERSION = '3';

export const ONBOARDING_QUESTIONS_QUESTIONNAIRE_CANONICAL = `${ONBOARDING_QUESTIONS_QUESTIONNAIRE_URL}|${ONBOARDING_QUESTIONS_QUESTIONNAIRE_VERSION}`;

/** Max length for free-text answers on onboarding-questions QuestionnaireResponses. */
export const ONBOARDING_QUESTIONS_TEXT_MAX_LENGTH = 500;

/**
 * Read {@link EXTENSIONS.ONBOARDING_COMPLETED} from a Patient. `undefined` means the extension is
 * absent (grandfathered); only an explicit `false` means questions are still owed.
 */
export const getPatientOnboardingCompleted = (
  patient: Pick<Patient, 'extension'> | undefined | null,
): boolean | undefined => {
  if (!patient) return undefined;
  const value = getExtensionValue(patient, EXTENSIONS.ONBOARDING_COMPLETED);
  return typeof value === 'boolean' ? value : undefined;
};

/**
 * Whether unanswered join-attribution questions should block app entry. Only patients whose Patient
 * resource carries `onboarding-completed: false` are gated — absent (pre-feature) and `true` are not.
 */
export const shouldRequirePatientOnboardingQuestions = (
  patient: Pick<Patient, 'extension'> | undefined | null,
): boolean => getPatientOnboardingCompleted(patient) === false;

/** Build the post-mutation `Patient.extension` list with onboarding-completed set to `completed`. */
export const withPatientOnboardingCompletedExtension = (
  extensions: Extension[] | undefined,
  completed: boolean,
): Extension[] => [
  ...(extensions ?? []).filter((ext) => ext.url !== EXTENSIONS.ONBOARDING_COMPLETED),
  { url: EXTENSIONS.ONBOARDING_COMPLETED, valueBoolean: completed },
];

export type QuestionnaireItemLabelSource = 'clinical' | 'patient';

/** Build a patient-facing label extension (English canonical + Spanish translation sibling). */
export const buildQuestionnaireItemPatientTextExtension = (
  text: string,
  textEs: string,
): ExtensionWithValueStringTranslations => ({
  url: QUESTIONNAIRE_ITEM_PATIENT_TEXT_URL,
  valueString: text,
  _valueString: { extension: [buildFhirTranslationExtension('es', textEs)] },
});

const clinicalItemLabel = (item: QuestionnaireItem, locale: string): string => {
  const translated = item as QuestionnaireItemWithTranslations;
  return resolveLocalizedString(item.text, translated._text, locale) ?? item.text ?? item.linkId;
};

/**
 * Label for a Questionnaire item: clinical (`item.text`) or patient-facing extension, falling back
 * to clinical when the patient extension is absent.
 */
export const resolveQuestionnaireItemLabel = (
  item: QuestionnaireItem,
  locale: string,
  source: QuestionnaireItemLabelSource = 'clinical',
): string => {
  if (source === 'clinical') return clinicalItemLabel(item, locale);

  const ext = getExtension(item, QUESTIONNAIRE_ITEM_PATIENT_TEXT_URL) as
    (ExtensionWithValueStringTranslations & { _valueString?: PrimitiveTranslationElement }) | undefined;
  if (!ext?.valueString) return clinicalItemLabel(item, locale);
  return resolveLocalizedString(ext.valueString, ext._valueString, locale) ?? clinicalItemLabel(item, locale);
};

const findItem = (
  qr: Pick<QuestionnaireResponse, 'item'> | undefined | null,
  linkId: string,
): QuestionnaireResponseItem | undefined => qr?.item?.find((item) => item.linkId === linkId);

const walkItems = (items: QuestionnaireItem[] | undefined, visit: (item: QuestionnaireItem) => void): void => {
  for (const item of items ?? []) {
    if (item.type === 'group') {
      walkItems(item.item, visit);
      continue;
    }
    if (item.type === 'display') continue;
    visit(item);
  }
};

/** Flatten questionnaire items (skipping groups/display) for form/sidebar rendering. */
export const flattenQuestionnaireInputItems = (questionnaire: Questionnaire): QuestionnaireItem[] => {
  const result: QuestionnaireItem[] = [];
  walkItems(questionnaire.item, (item) => result.push(item));
  return result;
};

const firstAnswer = (item: QuestionnaireResponseItem | undefined): QuestionnaireResponseItemAnswer | undefined =>
  item?.answer?.[0];

const localizedChoiceLabel = (
  item: QuestionnaireItem | undefined,
  answer: QuestionnaireResponseItemAnswer,
  locale: string,
): string | undefined => {
  if (!item?.answerOption?.length) return undefined;

  if (answer.valueCoding?.code) {
    const answerCoding = answer.valueCoding;
    const matched = item.answerOption.find((option) => {
      const optionCoding = option.valueCoding;
      if (!optionCoding || optionCoding.code !== answerCoding.code) return false;
      // Prefer a system match when both sides carry one; otherwise code alone is enough.
      if (answerCoding.system && optionCoding.system) {
        return optionCoding.system === answerCoding.system;
      }
      return true;
    });
    if (!matched?.valueCoding) return undefined;
    const translated = matched as QuestionnaireItemAnswerOptionWithTranslations;
    return resolveLocalizedString(matched.valueCoding.display, translated.valueCoding?._display, locale);
  }

  if (typeof answer.valueString === 'string') {
    const matched = item.answerOption.find((option) => option.valueString === answer.valueString);
    if (!matched) return undefined;
    const translated = matched as QuestionnaireItemAnswerOptionWithTranslations;
    return resolveLocalizedString(matched.valueString, translated._valueString, locale);
  }

  return undefined;
};

const formatPrimitiveAnswer = (answer: QuestionnaireResponseItemAnswer): string | null => {
  if (typeof answer.valueString === 'string') {
    const text = answer.valueString.trim();
    return text ? text.slice(0, ONBOARDING_QUESTIONS_TEXT_MAX_LENGTH) : null;
  }
  if (answer.valueBoolean !== undefined) return answer.valueBoolean ? 'Yes' : 'No';
  const codingDisplay = answer.valueCoding?.display?.trim();
  if (codingDisplay) return codingDisplay;
  const codingCode = answer.valueCoding?.code?.trim();
  if (codingCode) return codingCode;
  if (typeof answer.valueInteger === 'number') return String(answer.valueInteger);
  if (typeof answer.valueDecimal === 'number') return String(answer.valueDecimal);
  if (typeof answer.valueDate === 'string' && answer.valueDate) return answer.valueDate;
  return null;
};

/**
 * Human-readable answer for sidebar/display; blank answers return `null`.
 * When `item` + `locale` are provided, choice answers prefer the Questionnaire option's localized
 * display over the English `valueCoding.display` stamped on the QuestionnaireResponse.
 */
export const formatQuestionnaireResponseAnswer = (
  answer: QuestionnaireResponseItemAnswer | undefined,
  options?: { item?: QuestionnaireItem; locale?: string },
): string | null => {
  if (!answer) return null;

  const locale = options?.locale ?? 'en';
  const fromOption = localizedChoiceLabel(options?.item, answer, locale)?.trim();
  if (fromOption) return fromOption.slice(0, ONBOARDING_QUESTIONS_TEXT_MAX_LENGTH);

  return formatPrimitiveAnswer(answer);
};

export type OnboardingQuestionDisplay = {
  linkId: string;
  label: string;
  value: string;
};

/** Pair questionnaire labels with answered values for sidebar display (clinical wording). */
export const getOnboardingQuestionDisplays = (
  questionnaire: Questionnaire,
  response: QuestionnaireResponse | undefined | null,
  locale: string,
): OnboardingQuestionDisplay[] => {
  const displays: OnboardingQuestionDisplay[] = [];
  walkItems(questionnaire.item, (item) => {
    if (!isQuestionnaireItemEnabled(item, response)) return;
    const value = formatQuestionnaireResponseAnswer(firstAnswer(findItem(response, item.linkId)), {
      item,
      locale,
    });
    if (!value) return;
    displays.push({
      linkId: item.linkId,
      label: resolveQuestionnaireItemLabel(item, locale, 'clinical'),
      value,
    });
  });
  return displays;
};

/** Cap free-text answers before upsert so API and UI share one limit. */
export const truncateOnboardingQuestionText = (value: string): string =>
  value.slice(0, ONBOARDING_QUESTIONS_TEXT_MAX_LENGTH);

const hasAnswerValue = (response: QuestionnaireResponse | undefined | null, linkId: string): boolean => {
  const answers = findItem(response, linkId)?.answer ?? [];
  return answers.some(
    (a) =>
      (typeof a.valueString === 'string' && a.valueString.trim() !== '') ||
      a.valueBoolean !== undefined ||
      a.valueDate !== undefined ||
      a.valueDateTime !== undefined ||
      a.valueInteger !== undefined ||
      a.valueDecimal !== undefined ||
      a.valueQuantity?.value !== undefined ||
      a.valueCoding !== undefined,
  );
};

/**
 * Whether the patient has finished the patient-onboarding questions step. Items are optional on the
 * Questionnaire (clinician create/edit), but patient onboarding requires every enableWhen-visible
 * answerable item. No questionnaire → nothing to ask → complete.
 */
export const arePatientOnboardingQuestionsComplete = (
  questionnaire: Questionnaire | null | undefined,
  response: QuestionnaireResponse | null | undefined,
): boolean => {
  if (!questionnaire) return true;

  let complete = true;
  const walk = (items: QuestionnaireItem[] | undefined): void => {
    for (const item of items ?? []) {
      if (item.type === 'group') {
        if (!isQuestionnaireItemEnabled(item, response)) continue;
        walk(item.item);
        continue;
      }
      if (item.type === 'display') continue;
      if (!isQuestionnaireItemEnabled(item, response)) continue;
      if (!hasAnswerValue(response, item.linkId)) complete = false;
    }
  };
  walk(questionnaire.item);
  return complete;
};

/**
 * Questionnaire items are optional for clinicians; patient onboarding requires every answerable
 * item (including enableWhen follow-ups once shown). Groups/display are left unchanged.
 */
export const withAllAnswerableItemsRequired = (questionnaire: Questionnaire): Questionnaire => {
  const mapItems = (items: Questionnaire['item']): Questionnaire['item'] =>
    items?.map((item) => {
      if (item.type === 'group') {
        return { ...item, ...(item.item ? { item: mapItems(item.item) } : {}) };
      }
      if (item.type === 'display') return item;
      return { ...item, required: true };
    });
  return { ...questionnaire, item: mapItems(questionnaire.item) };
};

/**
 * Clinician create/edit never requires onboarding questions. Strip `required` so a stale Medplum
 * Questionnaire (still marked required from an older deploy) does not show asterisks or block save.
 */
export const withoutQuestionnaireItemRequired = (questionnaire: Questionnaire): Questionnaire => {
  const mapItems = (items: Questionnaire['item']): Questionnaire['item'] =>
    items?.map((item) => ({
      ...item,
      required: false,
      ...(item.item ? { item: mapItems(item.item) } : {}),
    }));
  return { ...questionnaire, item: mapItems(questionnaire.item) };
};

export type QuestionnaireResponseUpsertAnswer = {
  valueString?: string;
  valueBoolean?: boolean;
  valueCoding?: { system?: string; code: string; display?: string };
};

export type QuestionnaireResponseUpsertItem = {
  linkId: string;
  text?: string;
  answer?: QuestionnaireResponseUpsertAnswer[];
};

export type ToQuestionnaireResponseUpsertItemsOptions = {
  locale: string;
  labelSource?: QuestionnaireItemLabelSource;
  /** Cap free-text answers. Defaults to {@link ONBOARDING_QUESTIONS_TEXT_MAX_LENGTH}. */
  maxTextLength?: number;
};

const mapAnswerForUpsert = (
  answer: QuestionnaireResponseItemAnswer,
  maxTextLength: number,
): QuestionnaireResponseUpsertAnswer | undefined => {
  if (typeof answer.valueString === 'string') {
    return { valueString: answer.valueString.slice(0, maxTextLength) };
  }
  if (answer.valueBoolean !== undefined) return { valueBoolean: answer.valueBoolean };
  if (answer.valueCoding?.code) {
    return {
      valueCoding: {
        ...(answer.valueCoding.system ? { system: answer.valueCoding.system } : {}),
        code: answer.valueCoding.code,
        ...(answer.valueCoding.display ? { display: answer.valueCoding.display } : {}),
      },
    };
  }
  return undefined;
};

/**
 * Flatten enableWhen-visible answerable items into the upsert payload shape shared by patient and
 * admin QuestionnaireResponse writers.
 */
export const toQuestionnaireResponseUpsertItems = (
  questionnaire: Questionnaire,
  response: QuestionnaireResponse | undefined,
  options: ToQuestionnaireResponseUpsertItemsOptions,
): QuestionnaireResponseUpsertItem[] => {
  const labelSource = options.labelSource ?? 'clinical';
  const maxTextLength = options.maxTextLength ?? ONBOARDING_QUESTIONS_TEXT_MAX_LENGTH;
  const answerByLinkId = new Map((response?.item ?? []).map((item) => [item.linkId, item] as const));
  const items: QuestionnaireResponseUpsertItem[] = [];

  const walk = (qsItems: Questionnaire['item']): void => {
    for (const item of qsItems ?? []) {
      if (item.type === 'group') {
        if (!isQuestionnaireItemEnabled(item, response)) continue;
        walk(item.item);
        continue;
      }
      if (item.type === 'display') continue;
      if (!isQuestionnaireItemEnabled(item, response)) continue;
      const existing = answerByLinkId.get(item.linkId);
      const mappedAnswers = (existing?.answer ?? [])
        .map((answer) => mapAnswerForUpsert(answer, maxTextLength))
        .filter((answer): answer is QuestionnaireResponseUpsertAnswer => answer !== undefined);
      items.push({
        linkId: item.linkId,
        text: resolveQuestionnaireItemLabel(item, options.locale, labelSource),
        ...(mappedAnswers.length > 0 ? { answer: mappedAnswers } : {}),
      });
    }
  };
  walk(questionnaire.item);
  return items;
};
